import React from 'react';
import SearchableSelect from '../Form/SearchableSelect';
import { resolveAdminUserFieldStyle } from './adminUserFieldStyle';

/**
 * Shared basic identity + phone + role block for add/edit admin user pages (SRP).
 * WhatsApp, LinkedIn, and gender live in Additional Fields.
 * @param {'add' | 'edit' | 'public'} props.layoutVariant — field order and password copy differ slightly
 */
const AdminUserBasicInfoSection = ({
  layoutVariant,
  formData,
  onChange,
  roles,
  isStudentRole,
  getFieldStyle,
  afterRole = null,
  includePassword,
  roleHint,
}) => {
  const showPassword = includePassword ?? layoutVariant === 'add';
  const roleNote =
    roleHint !== undefined
      ? roleHint
      : 'Note: Education and placement details will appear once you select "student" as role.';
  const s = (value, required = false, fieldName) =>
    resolveAdminUserFieldStyle(getFieldStyle, value, required, fieldName);

  const nameRow = (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
      <input
        type="text"
        name="firstName"
        placeholder="First Name *"
        value={formData.firstName}
        onChange={onChange}
        required={layoutVariant !== 'edit'}
        className="px-4 py-3 theme-input rounded-lg theme-focus"
        style={s(formData.firstName, true, 'firstName')}
      />
      <input
        type="text"
        name="middleName"
        placeholder="Middle Name"
        value={formData.middleName}
        onChange={onChange}
        className="px-4 py-3 theme-input rounded-lg theme-focus"
        style={s(formData.middleName, false, 'middleName')}
      />
      <input
        type="text"
        name="lastName"
        placeholder="Last Name *"
        value={formData.lastName}
        onChange={onChange}
        required={layoutVariant !== 'edit'}
        className="px-4 py-3 theme-input rounded-lg theme-focus"
        style={s(formData.lastName, true, 'lastName')}
      />
    </div>
  );

  const emailPasswordRow = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <input
        type="email"
        name="email"
        placeholder="Email Address *"
        value={formData.email}
        onChange={onChange}
        required
        className="px-4 py-3 theme-input rounded-lg theme-focus"
        style={s(formData.email, true, 'email')}
      />
      {layoutVariant === 'edit' ? (
        <input
          type="password"
          name="password"
          placeholder="Password (leave blank to keep current)"
          value={formData.password}
          onChange={onChange}
          className="px-4 py-3 theme-input rounded-lg theme-focus"
          style={s(formData.password, false, 'password')}
        />
      ) : (
        <input
          type="text"
          name="mobile"
          placeholder="Phone *"
          value={formData.mobile || ''}
          onChange={onChange}
          className="px-4 py-3 theme-input rounded-lg theme-focus"
          style={s(formData.mobile, false, 'mobile')}
        />
      )}
    </div>
  );

  const passwordRow =
    showPassword ? (
      <div className="mb-4">
        <input
          type="password"
          name="password"
          placeholder="Password *"
          value={formData.password}
          onChange={onChange}
          required
          className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
          style={s(formData.password, false, 'password')}
        />
      </div>
    ) : null;

  const mobileRow =
    layoutVariant === 'edit' ? (
      <div className="mb-4">
        <input
          type="text"
          name="mobile"
          placeholder="Mobile Number"
          value={formData.mobile || ''}
          onChange={onChange}
          className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
          style={s(formData.mobile, false, 'mobile')}
        />
      </div>
    ) : null;

  const roleBlock = (
    <div className="mb-4">
      <SearchableSelect
        name="role"
        value={formData.role}
        onChange={onChange}
        required={layoutVariant !== 'edit'}
        className="w-full"
        placeholder="Select Role *"
        options={roles}
        getOptionValue={(role) => {
          const roleId = typeof role === 'object' ? role.id || role._id : role;
          return layoutVariant === 'edit' ? String(roleId) : roleId;
        }}
        getOptionLabel={(role) =>
          typeof role === 'object' ? role.value : role
        }
      />
      {roleNote ? <p className="text-sm text-gray-500 mt-2">{roleNote}</p> : null}
    </div>
  );

  const veBlock =
    layoutVariant === 'edit' && isStudentRole ? (
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
          VE Student ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="veStudentId"
          value={formData.veStudentId}
          onChange={onChange}
          placeholder="Enter VE Student ID (e.g., VE-2024-001)"
          required
          className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
          style={s(formData.veStudentId, true, 'veStudentId')}
        />
      </div>
    ) : null;

  return (
    <div id="auf-basic" className="auf-form-section scroll-mt-28 space-y-4 mb-6">
      <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Basic Information</h3>
      {nameRow}
      {emailPasswordRow}
      {layoutVariant === 'add' || layoutVariant === 'public' ? (
        <>
          {passwordRow}
          {roleBlock}
          {afterRole}
        </>
      ) : (
        <>
          {mobileRow}
          {roleBlock}
          {veBlock}
        </>
      )}
    </div>
  );
};

export default AdminUserBasicInfoSection;
